import React, { useEffect, useState } from 'react';
import { Form, Button, Row, Col, Card, Alert } from 'react-bootstrap';
import { FaPlus, FaTrash, FaPhone } from 'react-icons/fa';
import './ContactManager.css';
import './ContactManager.css';

/**
 * ContactManager Component
 * 
 * Manages multiple contacts with multiple phone numbers each
 * Used in both CustomerForm and SiteForm
 */
const ContactManager = ({ contacts = [], onChange, disabled = false }) => {
  const [localError, setLocalError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const updateIsMobile = () => setIsMobile(window.innerWidth <= 768);
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  /**
   * Add a new contact
   */
  const addContact = () => {
    const newContact = {
      id: Date.now(), // Temporary ID for UI
      name: '',
      email: '',
      phone_numbers: [{ number: '', type: 'primary' }]
    };
    onChange([...contacts, newContact]);
  };

  /**
   * Remove a contact
   */
  const removeContact = (contactIndex) => {
    const updatedContacts = contacts.filter((_, index) => index !== contactIndex);
    onChange(updatedContacts);
  };

  /**
   * Update contact details
   */
  const updateContact = (contactIndex, field, value) => {
    const updatedContacts = [...contacts];
    updatedContacts[contactIndex] = {
      ...updatedContacts[contactIndex],
      [field]: value
    };
    onChange(updatedContacts);
    setLocalError(null);
  };

  /**
   * Add phone number to a contact
   */
  const addPhoneNumber = (contactIndex) => {
    const updatedContacts = [...contacts];
    updatedContacts[contactIndex].phone_numbers.push({
      number: '',
      type: 'mobile'
    });
    onChange(updatedContacts);
  };

  /**
   * Remove phone number from a contact
   */
  const removePhoneNumber = (contactIndex, phoneIndex) => {
    const updatedContacts = [...contacts];
    updatedContacts[contactIndex].phone_numbers = updatedContacts[contactIndex].phone_numbers.filter(
      (_, index) => index !== phoneIndex
    );
    onChange(updatedContacts);
  };

  /**
   * Update phone number details
   */
  const updatePhoneNumber = (contactIndex, phoneIndex, field, value) => {
    const updatedContacts = [...contacts];
    updatedContacts[contactIndex].phone_numbers[phoneIndex] = {
      ...updatedContacts[contactIndex].phone_numbers[phoneIndex],
      [field]: value
    };
    onChange(updatedContacts);
  };

  /**
   * Validate email format
   */
  const isValidEmail = (email) => {
    if (!email.trim()) return true; // Empty email is valid
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  return (
    <div className="contact-manager">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">Contacts</h6>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={addContact}
          disabled={disabled}
        >
          <FaPlus className="me-1" />
          <span className="button-text">Add Contact</span>
        </Button>
      </div>

      {localError && (
        <Alert variant="danger" dismissible onClose={() => setLocalError(null)}>
          {localError}
        </Alert>
      )}

      {contacts.length === 0 && (
        <div className="text-center text-muted py-3">
          <p>No contacts added yet. Click "Add Contact" to get started.</p>
        </div>
      )}

      {contacts.map((contact, contactIndex) => (
        <div key={contact.id || contactIndex} style={{ marginBottom: '2rem', width: '100%' }}>
          <Card className="contact-card" style={{ width: '100%' }}>
            <Card.Header
              className="d-flex justify-content-between align-items-center"
              style={{
                padding: '1rem 1.25rem',
                backgroundColor: 'var(--sf-surface)',
                borderBottom: '2px solid var(--sf-border)'
              }}
            >
              <h6 className="mb-0" style={{ fontWeight: 600, fontSize: '1.05rem' }}>Contact #{contactIndex + 1}</h6>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => removeContact(contactIndex)}
                disabled={disabled}
                title="Remove this entire contact"
              >
                <FaTrash className="me-1" />
                {!isMobile && <span className="button-text">Remove</span>}
              </Button>
            </Card.Header>

            <Card.Body style={{ padding: '1.5rem' }}>
              {/* Name Field */}
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>
                  Name *
                </Form.Label>
                <Form.Control
                  type="text"
                  value={contact.name}
                  onChange={(e) => updateContact(contactIndex, 'name', e.target.value)}
                  placeholder="Contact full name"
                  disabled={disabled}
                  required
                />
              </Form.Group>

              {/* Email Field */}
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>
                  Email
                </Form.Label>
                <Form.Control
                  type="email"
                  value={contact.email}
                  onChange={(e) => updateContact(contactIndex, 'email', e.target.value)}
                  placeholder="contact@email.com"
                  disabled={disabled}
                  isInvalid={contact.email && !isValidEmail(contact.email)}
                />
                <Form.Control.Feedback type="invalid">
                  Please enter a valid email address.
                </Form.Control.Feedback>
              </Form.Group>

              {/* Second Row: Phone Numbers (Full Width) */}
              <Row>
                <Col xs={12}>
                  <div>
                    <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '0.75rem' }}>
                      <Form.Label className="mb-0" style={{ fontWeight: 600 }}>
                        Phone Numbers
                      </Form.Label>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => addPhoneNumber(contactIndex)}
                        disabled={disabled}
                        style={{ padding: '0.5rem 0.75rem' }}
                      >
                        <FaPhone className="me-1" />
                        <span className="button-text">Add Phone</span>
                      </Button>
                    </div>

                    {contact.phone_numbers?.map((phone, phoneIndex) => {
                      return (
                        <div key={phoneIndex} className="phone-number-row">
                          <div className="phone-number-inputs">
                            <div className="phone-type-wrapper">
                              <Form.Label>Type</Form.Label>
                              <Form.Select
                                value={phone.type}
                                onChange={(e) => updatePhoneNumber(contactIndex, phoneIndex, 'type', e.target.value)}
                                disabled={disabled}
                                size="sm"
                              >
                                <option value="primary">Primary</option>
                                <option value="mobile">Mobile</option>
                                <option value="office">Office</option>
                                <option value="home">Home</option>
                                <option value="fax">Fax</option>
                              </Form.Select>
                            </div>
                            <div className="phone-number-wrapper">
                              <Form.Label>Number</Form.Label>
                              <Form.Control
                                type="tel"
                                value={phone.number}
                                onChange={(e) => updatePhoneNumber(contactIndex, phoneIndex, 'number', e.target.value)}
                                placeholder="(555) 123-4567"
                                disabled={disabled}
                                size="sm"
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {(!contact.phone_numbers || contact.phone_numbers.length === 0) && (
                      <div className="text-muted text-center" style={{ padding: '0.5rem 0', fontSize: '0.875rem' }}>
                        No phones added
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </div>
      ))}
    </div>
  );
};

export default ContactManager;
